import React, { useState, useRef } from "react";
import { Col, Row, Alert } from "reactstrap";
import _ from "lodash";
import * as SchemaLib from "@repoflow/schema";

import * as Loading from "@repoflow/components/loading";
import { useQuery } from "@apollo/client";
import { useEffect } from "react";

import { ObjectComponent, getField } from "@repoflow/form-schema/widgets/object";

import {
  Provider as SchemaProvider,
  useSchemaContext,
} from "@repoflow/form-schema/schema-config";
import "./style.css";

export const getListOffset = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const id = queryParams.get("id");
  const updatedAt = queryParams.get("updatedAt");

  return id && updatedAt ? { id, updatedAt } : null;
};

export const List = (props) => {
  const { schema, config, widgets: outterWidgets } = props;

  return (
    <SchemaProvider schema={schema} widgets={outterWidgets} config={config}>
      <ListHandler {...props} />
    </SchemaProvider>
  );
};

const ListHandler = (props) => {
  const {
    mode = "table",
    query,
    variables,
    path,
    onData,
    orderBy: initialOrderBy = "updatedAt",
    orderMode: initialOrderMode = "desc",
    wrapper: Wrapper = React.Fragment,
    listTag = "list",
    fetchPolicy,
    mapItem,
  } = props;

  const [state, setState] = useState({
    locked: true,
    current: null,
    result: null,
    orderBy: initialOrderBy,
    orderMode: initialOrderMode,
  });

  const { orderBy, orderMode } = state;
  const toggleOrderMode = () =>
    setState((state) => ({
      ...state,
      orderMode: orderMode === "asc" ? "desc" : "asc",
      result: null,
      current: null,
    }));

  const setOrderBy = (orderBy) =>
    setState((state) => ({ ...state, orderBy, result: null, current: null }));

  const { loading, error, data, refetch } = useQuery(query, {
    fetchPolicy,
    variables: {
      ...variables,
      input: {
        last: state.current,
        orderBy,
        orderMode,
      },
    },
  });

  const getResults = ({ result }) =>
    _.map(
      _.keys(result),
      /*orderMode === "asc"
        ? _.keys(result).sort()
        : _.keys(result).sort().reverse()*/ (k) => result[k]
    ).flat();

  const list = data ? _.get(data, path + "." + listTag) : null;

  // Every query already selects the `last` cursor and every page accumulates
  // into state.result keyed by list.id — the mechanism was complete, nothing
  // ever advanced the cursor, so these lists only ever showed their first page
  // while still claiming "End of results" was somewhere below.
  const loadMore = () => {
    if (list && list.last) {
      setState((state) => ({ ...state, current: list.last, locked: true }));
    }
  };

  useEffect(() => {

    if (data) {
      const list = _.get(data, path + "." + listTag);

      setState((state) => ({
        ...state,
        result: {
          ...state.result,
          [list.id]: mapItem ? list.items.map(mapItem) : list.items,
        },
        data,
      }));
    }
  }, [data]);

  useEffect(() => {
    if (state.result && data) {
      const results = getResults(state);
      onData && onData(results, data);

      setState((state) => ({
        ...state,
        locked: false,
      }));
    }
  }, [state.result]);

  // The raw GraphQL message used to be returned as a bare string in place of
  // the whole list, which reads as a stray line of text on the page and offers
  // no way to retry. The detail goes to the console instead.
  if (error) {
    console.error("list-cursor:", error);
    return (
      <Alert color="danger">
        This list could not be loaded.{" "}
        <button
          type="button"
          className="btn btn-link p-0 align-baseline"
          onClick={() => refetch()}
        >
          Try again
        </button>
      </Alert>
    );
  }

  return (
    <HandleResults
      state={state}
      toggleOrderMode={toggleOrderMode}
      setOrderBy={setOrderBy}
      getResults={getResults}
      loading={loading}
      refetch={refetch}
      data={data}
      list={list}
      loadMore={loadMore}
      {...props}
    />
  );
};

const HandleResults = (props) => {
  const {
    state,
    toggleOrderMode,
    setOrderBy,
    getResults,
    mode = "table",
    refetch,
    variables,
    target,
    attributes,
    component: Component,
    content: Content,
    path,
    onData,
    orderBy: initialOrderBy = "updatedAt",
    orderMode: initialOrderMode = "desc",
    height,
    wrapper: Wrapper = React.Fragment,
    listTag = "list",
    fetchPolicy,
    mapItem,
    orderFields = [],
    loading,
    data,
    list,
    loadMore,
    empty = "Nothing here yet.",
  } = props;

  const context = useSchemaContext();
  const { config, schema } = context;

  const { orderBy, orderMode } = state;

  if (!state.result) {
    return (
      <div className="mt-4">
        <Loading.Content />
      </div>
    );
  }

  const results = getResults(state);

  const resolvedType =
    target && schema ? SchemaLib.resolve(schema, target) : null;
  const itemsField = resolvedType
    ? getField("listing", resolvedType, { parent: null, values: null }, context)
    : [];

  const targetConfig = resolvedType
    ? config
      ? config[resolvedType.id]
      : null
    : null;
  const targetFields =
    targetConfig && targetConfig.fields
      ? targetConfig.fields
      : resolvedType && itemsField
        ? itemsField.fields.map(({ name }) => name)
        : null;
  const fieldColumns =
    resolvedType && itemsField && targetFields
      ? _.reduce(
        targetFields,
        (res, fieldConfig) => {
          const fieldProp = _.find(itemsField.fields, (field) => {
            return (
              fieldConfig === field.name ||
              fieldConfig.property === field.name
            );
          });

          if (!fieldProp) {
            throw new Error(
              "invalid.config:" +
              (fieldConfig ? fieldConfig.property : fieldConfig)
            );
          }

          res.push(fieldProp);
          return res;
        },
        []
      )
      : null;

  const contentProps = {
    attributes,
    variables,
    data: state.data,
    refetch
  };

  return (
    <>
      {mode === "table" ? (
        <div className="table-responsive-md">
          <table className="table table-striped table-sm">
            <thead className="thead-dark">
              <tr>
                {Content && <th></th>}
                {itemsField ? (
                  _.map(fieldColumns, (field, idx) => {
                    const { name, property, config } = field;

                    return (
                      <th className="col-xs-6 col-sm-4 col-md-3" key={idx}>
                        {name === orderBy && (
                          <button
                            onClick={() => toggleOrderMode()}
                            className="btn btn-link"
                          >
                            {orderMode === "asc" ? (
                              <i className="fa fa-sort-asc"></i>
                            ) : (
                              <i className="fa fa-sort-desc"></i>
                            )}
                          </button>
                        )}
                        {name !== orderBy && orderFields.includes(name) && (
                          <button
                            onClick={() => setOrderBy(name)}
                            className="btn btn-link"
                          >
                            <i className="fa fa-sort"></i>
                          </button>
                        )}{" "}
                        {(config && config.label) || property.label || name}
                      </th>
                    );
                  })
                ) : (
                  <th></th>
                )}
              </tr>
            </thead>

            <tbody>
              {results.length === 0 && !loading ? (
                <tr>
                  <td
                    className="text-muted text-center"
                    colSpan={
                      (fieldColumns ? fieldColumns.length : 1) +
                      (Content ? 1 : 0)
                    }
                  >
                    {empty}
                  </td>
                </tr>
              ) : null}
              {_.map(results, (item) => {
                const { id } = item;
                return (
                  <tr key={id}>
                    {Content ? (
                      <td>
                        <Content {...contentProps} item={item} />
                      </td>
                    ) : null}

                    <ObjectComponent
                      mode={mode}
                      target={target}
                      parent={null}
                      value={item}
                      readOnly={true}
                    />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <Wrapper>
          {results.length === 0 && !loading ? (
            <div className="text-muted">{empty}</div>
          ) : null}
          {_.map(results, (item) => {
            const { id } = item;
            return Component ? (
              <Component
                {...contentProps}
                key={id}
                item={item}
                content={Content}
              />
            ) : (
              <React.Fragment key={id}>
                <Row>
                  <Col md={Content ? 8 : 12}>
                    <ObjectComponent
                      target={target}
                      parent={null}
                      value={item}
                      readOnly={true}
                    />
                  </Col>
                  {Content ? (
                    <Col>
                      <Content {...contentProps} item={item} />
                    </Col>
                  ) : null}
                </Row>
                <hr />
              </React.Fragment>
            );
          })}
        </Wrapper>
      )}

      {loading && (
        <div className="mt-4">
          <Loading.Content />
        </div>
      )}

      {!loading && list && list.last && loadMore && (
        <Row>
          <Col>
            <div className="text-center">
              <button
                type="button"
                className="btn btn-link"
                onClick={() => loadMore()}
              >
                Load more...
              </button>
            </div>
          </Col>
        </Row>
      )}

      {/* Only worth saying once there is something to be at the end of —
          an empty list already says so in the table body. */}
      {list && !list.last && results.length > 0 && (
        <Row>
          <Col>
            <div className="text-center text-muted">End of results</div>
          </Col>
        </Row>
      )}
    </>
  );
};
